import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import Head from 'next/head';
import get from 'lodash/get';
import capitalize from 'lodash/capitalize';
import buildConfig from 'app/utils/buildConfig';

import analytics from 'app/utils/analytics';
import mixpanel, { trackedEvents } from 'app/utils/mixpanel';
import api from 'app/api/productApi';
import formatTimeago from 'app/utils/formatTimeago';

// Components.
import { Button, Space } from 'app/seedly-component-library';
import Avatar from 'seedly-component-library/avatar-new';
import ProductPassport from 'app/components/product/ProductPassport';

// Styles.
import styled from 'styled-components';

const Container = styled.div`
  border: 1px solid ${props => props.theme.colors.neutral4};
  border-radius: 5px;
  background: white;
`;

const Header = styled.h4`
  color: ${props => props.theme.colors.seedlyBlue};
  border-bottom: 1px solid ${props => props.theme.colors.neutral4};
  height: 40px;
  line-height: 40px;
  padding: 0 10px;
`;

const UserReviewContainer = styled.div`
  display: grid;
  grid-template-columns: max-content 1fr;
  grid-column-gap: 10px;
  grid-row-gap: 10px;
  padding: 0 10px 10px;
`;

const Text = styled.div`
  color: #4a4a4a;
  white-space: nowrap;
  overflow: hidden;
  width: 100%;

  max-width: calc(100%);
  text-overflow: ellipsis;
  display: block;
  word-wrap: normal;
  min-width: 0;

  @media screen and (min-color-index: 0) and(-webkit-min-device-pixel-ratio:0) {
    @media {
      -webkit-line-clamp: 1;
      -webkit-box-orient: vertical;
      display: -webkit-box;
      white-space: normal;
    }
  }

  @media not all and (min-resolution: 0.001dpcm) {
    @media {
      -webkit-line-clamp: 1;
      -webkit-box-orient: vertical;
      display: -webkit-box;
      white-space: normal;
    }
  }
`;

const ButtonRow = styled(Space)`
  padding-left: 10px;
  padding-bottom: 10px;
`;

const UserInfo = styled.div`
  color: #4a4a4a;
  font-size: 12px;
`;

const UserReview = ({ reviewText, user }) => {
  const isValid = reviewText && user && Object.values(user).length;
  if (!isValid) return null;

  const dateAgo = formatTimeago(user.updatedAt, { cutoff: false });
  return (
    <UserReviewContainer>
      <Avatar src={user.image} name={user.name} />
      <Text>
        <Text>{reviewText}</Text>

        <UserInfo>
          <span>{user.name}</span>&nbsp;&middot;&nbsp;
          <span>{dateAgo}</span>
        </UserInfo>
      </Text>
    </UserReviewContainer>
  );
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI.
    return { hasError: !!error };
  }

  render() {
    if (this.state.hasError) {
      // You can render any custom fallback UI.
      return <div />;
    }

    return this.props.children;
  }
}

const Product = () => {
  const router = useRouter();
  const [product, setProduct] = useState(null);
  const [error, setError] = useState(null);
  const { product_slug: productSlug, category_slug: categorySlug } =
    router.query || {};

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const result = await api.getProducts(
          {
            slugs: productSlug,
            category_slug: categorySlug,
          },
          {},
        );
        setProduct(get(result, 'data.data[0]', {}));
      } catch (err) {
        setError(err);
      }
    };
    fetchProduct();
  }, []);

  if (error) {
    return (
      <div>
        Go to <a href="https://www.seedly.sg">Seedly.sg</a>
      </div>
    );
  }

  const productInfo = get(product, 'details.info', []).find(
    item => item.name === 'url',
  );
  const outboundUrl = get(productInfo, 'value', '');
  const detailUrl = `${buildConfig.apiHost}/reviews/${categorySlug}/${productSlug}`;
  const handleClickVisit = evt => {
    evt.preventDefault();
    analytics.trackOutbound({
      from: 'Product Snippet - Outbound',
      url: outboundUrl,
    });
    mixpanel.track(trackedEvents.Outbound, {
      'Category Name': capitalize(categorySlug.replace(/-/g, ' ')),
      'Product Name': capitalize(productSlug.replace(/-/g, ' ')),
      'Plan Name': '',
    });
    parent.window.open(outboundUrl, '_blank');
  };

  const handleClickSeeMore = evt => {
    evt.preventDefault();
    analytics.trackOutbound({
      from: 'Article Widget - Snippet',
      url: `/${categorySlug}/${productSlug}`,
    });
    mixpanel.track(trackedEvents.Outbound, {
      'Category Name': capitalize(categorySlug.replace(/-/g, ' ')),
      'Product Name': capitalize(productSlug.replace(/-/g, ' ')),
      'Plan Name': '',
    });
    parent.window.open(detailUrl, '_blank');
  };

  return (
    product && (
      <Container>
        <Head>
          <meta name="robots" content="noindex,follow" />
        </Head>
        <Header>Reviews from Seedly Community</Header>
        <div style={{ padding: '15px 10px' }}>
          <ProductPassport product={product} disableLink />
        </div>
        <UserReview {...product.review} />
        <ButtonRow size="md">
          <a href={detailUrl} onClick={handleClickSeeMore} target="_top">
            <Button>More SeedlyReviews</Button>
          </a>
          {outboundUrl && (
            <a href={outboundUrl} onClick={handleClickVisit} target="_top">
              <Button variant="outline">Visit Site</Button>
            </a>
          )}
        </ButtonRow>
      </Container>
    )
  );
};

const View = () => (
  <ErrorBoundary>
    <Product />
  </ErrorBoundary>
);

export default View;
